import React from "react";
import { Formik, Form } from "formik";
import ModalComponent from "../../../components/Modal";
import CustomDropdown from "../../../components/CustomDropdown";
import TextInput from "../../../components/TextInput";
import { FaUpload, FaFile, FaTrash } from "react-icons/fa";
import TextArea from "../../../components/TextArea";

const AssociateSubmissionModal = ({
  show,
  onClose,
  isLoadingCandidates,
  candidates,
  selectedCandidate,
  onSelectCandidate,
  formValues,
  onFieldChange,
  onSubmit,
  isSubmitting,
  candidateResumes,
  resumeAction,
  onResumeActionChange,
  selectedResumeId,
  onSelectResume,
  onResumeFileChange,
  resumeFile,
  isCheckingResume,
  resumeError,
  onFileChange,
  onDeleteFile,
}) => {
  const isCandidateSelected = Boolean(selectedCandidate?.value);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <ModalComponent show={show} onClose={onClose} title="Add Submission">
      <div className="max-h-[60vh] overflow-y-auto pr-2">
        {isLoadingCandidates ? (
          <div className="py-6 text-center text-sm text-gray-500">Loading candidates...</div>
        ) : (
          <Formik
          initialValues={formValues}
          enableReinitialize
          validate={(values) => {
            const errors = {};
            if (!selectedCandidate?.value) {
              errors.candidate = "Candidate is required";
            }
            if (!values.rate || !values.rate.trim()) {
              errors.rate = "Rate is required";
            }
            if (!values.rateType || !values.rateType.trim()) {
              errors.rateType = "Rate type is required";
            }
            return errors;
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await onSubmit(values);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched, handleChange, isSubmitting: formikSubmitting, setFieldValue }) => {
            const isFormSubmitting = isSubmitting || formikSubmitting;

            return (
              <Form className="mt-6 space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:items-end">
                  <div className="flex flex-col gap-2">
                    <CustomDropdown
                      value={selectedCandidate}
                      name="candidate"
                      handleChange={(option) => onSelectCandidate(option || null)}
                      placeholder="Select Candidate"
                      options={candidates.map((candidate) => ({
                        value: candidate.id,
                        label: candidate.candidateName || `Candidate ${candidate.id}`,
                      }))}
                      handleGetOptionValue={(opt) => opt.value}
                      handleGetOptionName={(opt) => opt.label}
                      customClasses="w-full"
                      isDisabled={isFormSubmitting || isCheckingResume}
                    />
                    {errors.candidate && touched.candidate && (
                      <div className="text-red-500 text-xs mt-1">{errors.candidate}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                  <TextInput
                    name="rate"
                    legend="Rate"
                    placeholder="Rate"
                    value={values.rate}
                    handleChange={handleChange}
                    type="text"
                    classes="w-full"
                  />
                    {errors.rate && touched.rate && (
                      <div className="text-red-500 text-xs mt-1">{errors.rate}</div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                  <TextInput
                    name="rateType"
                    legend="Rate Type"
                    placeholder="Rate Type"
                    value={values.rateType}
                    handleChange={handleChange}
                    type="text"
                    classes="w-full"
                  />
                    {errors.rateType && touched.rateType && (
                      <div className="text-red-500 text-xs mt-1">{errors.rateType}</div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">Resume</label>
                  {isCheckingResume ? (
                    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                      Checking candidate resume availability...
                    </div>
                  ) : (
                    <>
                      {!isCandidateSelected ? (
                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                          Select a candidate to manage resumes.
                        </div>
                      ) : candidateResumes.length > 0 ? (
                        <div className="space-y-3 rounded-md border border-gray-200 p-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="radio"
                              id="resume-use-existing"
                              name="resumeAction"
                              value="USE_EXISTING"
                              checked={resumeAction === "USE_EXISTING"}
                              onChange={() => onResumeActionChange?.("USE_EXISTING")}
                              disabled={isFormSubmitting}
                              className="h-4 w-4"
                            />
                            <label htmlFor="resume-use-existing" className="text-sm text-gray-700">
                              Use existing resume
                            </label>
                          </div>
                          {resumeAction === "USE_EXISTING" && (
                            <CustomDropdown
                              value={candidateResumes
                                .map((doc) => ({ value: doc.id, label: doc.originalFilename || doc.fileName || `Resume ${doc.id}` }))
                                .find((option) => String(option.value) === String(selectedResumeId || ""))}
                              name="selectedResumeId"
                              handleChange={(option) => onSelectResume?.(option?.value || "")}
                              placeholder="Select a resume"
                              options={candidateResumes.map((doc) => ({
                                value: doc.id,
                                label: doc.originalFilename || doc.fileName || `Resume ${doc.id}`,
                              }))}
                              handleGetOptionValue={(opt) => opt.value}
                              handleGetOptionName={(opt) => opt.label}
                              isDisabled={isFormSubmitting}
                              customClasses="w-full"
                            />
                          )}
                          <div className="flex items-center gap-2">
                            <input
                              type="radio"
                              id="resume-upload-new"
                              name="resumeAction"
                              value="UPLOAD_NEW"
                              checked={resumeAction === "UPLOAD_NEW"}
                              onChange={() => onResumeActionChange?.("UPLOAD_NEW")}
                              disabled={isFormSubmitting}
                              className="h-4 w-4"
                            />
                            <label htmlFor="resume-upload-new" className="text-sm text-gray-700">
                              Upload a different resume
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                          No resume found for this candidate. Please upload a resume to proceed.
                        </div>
                      )}
                      {/* File upload section - show when candidate is selected and either UPLOAD_NEW is selected or no resumes exist */}
                      {isCandidateSelected && (resumeAction === "UPLOAD_NEW" || candidateResumes.length === 0) && (
                        <div className="mt-2 space-y-2 rounded-md border border-gray-200 p-2">
                          {/* Display newly selected file */}
                          {resumeFile && (
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-gray-600 mb-2">New Resume File:</h4>
                              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="flex-shrink-0">
                                    <FaFile className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {resumeFile.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatFileSize(resumeFile.size || 0)}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={onDeleteFile}
                                    className="text-rose-500 hover:text-rose-700 transition-colors p-1 rounded"
                                    title="Remove file"
                                    disabled={isFormSubmitting}
                                  >
                                    <FaTrash className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* File upload area - show when no file is selected */}
                          {!resumeFile && (
                            <div className="mt-1">
                              <label
                                htmlFor="resume-file-input-associate"
                                className={`relative block border-2 border-dashed rounded-lg transition-all duration-200 border-gray-300 hover:border-blue-500/50 bg-gray-50/30 ${isFormSubmitting || !isCandidateSelected ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                              >
                                <div className="p-2 text-center">
                                  <div className="flex justify-center mb-1">
                                    <div className="p-1 rounded-full bg-gray-100">
                                      <FaUpload className="w-4 h-4 text-gray-400" />
                                    </div>
                                  </div>

                                  <h4 className="text-xs font-medium text-gray-700 mb-0.5">
                                    Select a resume file
                                  </h4>

                                  <p className="text-xs text-gray-500 mb-1.5">
                                    Click to browse and select a resume file
                                  </p>

                                  <div className={`inline-flex items-center px-2.5 py-1 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors pointer-events-none`}>
                                    <FaUpload className="w-3 h-3 mr-1" />
                                    Choose File
                                  </div>
                                  <p className="text-xs text-gray-400 mt-1">
                                    Supported formats: PDF, DOCX, PNG, JPG, JPEG, BMP, GIF (Max 10MB)
                                  </p>
                                </div>
                                <input
                                  id="resume-file-input-associate"
                                  type="file"
                                  accept=".pdf,.docx,.png,.jpg,.jpeg,.bmp,.gif"
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                  onChange={onFileChange}
                                  disabled={isFormSubmitting || !isCandidateSelected}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {isCandidateSelected && resumeError && (
                    <p className="text-xs text-red-600 mt-2">{resumeError}</p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
               
                  <TextArea
                    name="notes"
                    legend="Notes"
                    placeholder="Notes"
                    value={values.notes}
                    handleChange={handleChange}
                    classes="w-full"
                    rows={4}
                    isDisabled={isFormSubmitting}
                  />
                  {errors.notes && touched.notes && (
                    <div className="text-red-500 text-xs mt-1">{errors.notes}</div>
                  )}
                  <div className="text-xs text-gray-500 text-right">
                    {(values.notes || "").length}/500 characters
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={isFormSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-md bg-[#201F1E] text-white text-sm font-medium hover:bg-[#201F1E] focus:outline-none focus:ring-2 focus:ring-[#201F1E] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={isFormSubmitting || !selectedCandidate?.value}
                  >
                    {isFormSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </Form>
            );
          }}
          </Formik>
        )}
      </div>
    </ModalComponent>
  );
};

export default AssociateSubmissionModal;

